class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Free Gift Progress Meter functionality
class CartGiftProgress {
  constructor() {
    this.giftThresholdAUD = 10000; // $100 AUD in cents
    this.cartUpdateUnsubscriber = null;
    this.init();
    this.setupCartUpdateListener();
  }

  init() {
    this.updateProgress();
  }

  setupCartUpdateListener() {
    // Listen for cart updates using the pub/sub system
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // Use debounce if available, otherwise use setTimeout as fallback
      const updateHandler = typeof debounce !== 'undefined' 
        ? debounce((event) => {
            // Update data attributes if cart drawer was re-rendered
            const progressElement = document.querySelector('.cart-gift-progress');
            if (progressElement && event && event.cartData) {
              const cartTotal = event.cartData.total_price || 0;
              const currency = event.cartData.currency || 'AUD';
              progressElement.dataset.cartTotal = cartTotal;
              progressElement.dataset.currency = currency;
            }
            this.updateProgress();
          }, 150)
        : (event) => {
            setTimeout(() => {
              const progressElement = document.querySelector('.cart-gift-progress');
              if (progressElement && event && event.cartData) {
                const cartTotal = event.cartData.total_price || 0;
                const currency = event.cartData.currency || 'AUD';
                progressElement.dataset.cartTotal = cartTotal;
                progressElement.dataset.currency = currency;
              }
              this.updateProgress();
            }, 150);
          };

      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        updateHandler(event);
      });
    }
  }

  convertToAUD(amountCents, fromCurrency) {
    if (fromCurrency === 'AUD') {
      return amountCents;
    }

    try {
      // Try to get currency rate from Shopify's currency object
      if (window.Shopify && window.Shopify.currency) {
        // Check if we have access to rates
        if (window.Shopify.currency.rates && window.Shopify.currency.rates[fromCurrency] && window.Shopify.currency.rates['AUD']) {
          const fromRate = window.Shopify.currency.rates[fromCurrency];
          const toRate = window.Shopify.currency.rates['AUD'];
          return Math.round((amountCents / fromRate) * toRate);
        }
        
        // Alternative: use active currency rate if available
        if (window.Shopify.currency.active && window.Shopify.currency.active !== 'AUD') {
          // For multicurrency, Shopify typically stores rates
          // This is a simplified conversion - you may need to adjust based on your setup
          const rate = window.Shopify.currency.rate || 1;
          return Math.round(amountCents / rate);
        }
      }

      // Fallback: fetch current cart to get AUD equivalent
      // This will be handled by updating from the cart data
      return amountCents;
    } catch (error) {
      console.error('Error converting currency:', error);
      return amountCents;
    }
  }

  updateProgress() {
    const progressElement = document.querySelector('.cart-gift-progress');
    if (!progressElement) return;

    // Get cart total from data attribute or try to get from DOM
    let cartTotalCents = parseInt(progressElement.dataset.cartTotal) || 0;
    let currentCurrency = progressElement.dataset.currency || 'AUD';
    const thresholdAUD = parseInt(progressElement.dataset.thresholdAud) || this.giftThresholdAUD;

    // If data attributes are not set, try to get from cart total element
    if (!cartTotalCents) {
      const cartTotalElement = document.querySelector('.totals__total-value');
      if (cartTotalElement) {
        // Extract number from formatted price (this is a fallback)
        const priceText = cartTotalElement.textContent.replace(/[^\d.]/g, '');
        cartTotalCents = Math.round(parseFloat(priceText) * 100);
      }
    }

    // Convert to AUD
    const cartTotalAUDCents = this.convertToAUD(cartTotalCents, currentCurrency);

    // Calculate progress
    const progress = Math.min((cartTotalAUDCents / thresholdAUD) * 100, 100);
    const remainingCents = Math.max(thresholdAUD - cartTotalAUDCents, 0);
    const isQualified = cartTotalAUDCents >= thresholdAUD;

    // Store current values to prevent unnecessary updates
    const currentProgress = parseFloat(progressElement.dataset.lastProgress || '0');
    const currentQualified = progressElement.dataset.qualified === 'true';
    const currentRemaining = progressElement.dataset.lastRemaining || '0';

    // Update qualified state
    progressElement.dataset.qualified = isQualified;
    progressElement.dataset.lastProgress = progress.toFixed(1);
    progressElement.dataset.lastRemaining = remainingCents.toString();

    // Update progress bar only if changed
    const fillElement = progressElement.querySelector('.cart-gift-progress__fill');
    if (fillElement && Math.abs(currentProgress - progress) > 0.1) {
      fillElement.style.width = `${progress}%`;
      fillElement.dataset.progress = progress.toFixed(1);
    }

    // Update remaining amount or show "Added to Cart" only if changed
    const remainingContainer = progressElement.querySelector('.cart-gift-card__remaining');
    if (remainingContainer) {
      // Only update if qualified state changed or remaining amount changed significantly
      const remainingChanged = Math.abs(parseInt(currentRemaining) - remainingCents) > 1;
      
      if (isQualified !== currentQualified || (isQualified && remainingChanged)) {
        if (isQualified) {
          // Show "Added to Cart" with checkmark
          const checkmarkSvg = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-checkmark" viewBox="0 0 12 9"><path fill="currentColor" fill-rule="evenodd" d="M11.35.643a.5.5 0 0 1 .006.707l-6.77 6.886a.5.5 0 0 1-.719-.006L.638 4.845a.5.5 0 1 1 .724-.69l2.872 3.011 6.41-6.517a.5.5 0 0 1 .707-.006z" clip-rule="evenodd"/></svg>';
          remainingContainer.innerHTML = `<span class="cart-gift-card__checkmark">${checkmarkSvg}</span><strong class="cart-gift-card__qualified">Added to Cart</strong>`;
        } else {
          // Show "spend $X more"
          const remainingElement = remainingContainer.querySelector('[data-remaining-amount]');
          if (remainingElement) {
            // Convert remaining AUD back to current currency for display
            const remainingInCurrentCurrency = this.convertFromAUD(remainingCents, currentCurrency);
            const formattedAmount = this.formatCurrency(remainingInCurrentCurrency, currentCurrency);
            remainingElement.textContent = formattedAmount;
          } else {
            // Recreate the element if it doesn't exist
            const remainingInCurrentCurrency = this.convertFromAUD(remainingCents, currentCurrency);
            const formattedAmount = this.formatCurrency(remainingInCurrentCurrency, currentCurrency);
            remainingContainer.innerHTML = `spend <strong data-remaining-amount style="color: #d73044;">${formattedAmount}</strong> more`;
          }
        }
      } else if (!isQualified && remainingChanged) {
        // Only update the amount text, don't recreate the whole structure
        const remainingElement = remainingContainer.querySelector('[data-remaining-amount]');
        if (remainingElement) {
          const remainingInCurrentCurrency = this.convertFromAUD(remainingCents, currentCurrency);
          const formattedAmount = this.formatCurrency(remainingInCurrentCurrency, currentCurrency);
          remainingElement.textContent = formattedAmount;
        }
      }
    }
  }

  convertFromAUD(amountAUDCents, toCurrency) {
    if (toCurrency === 'AUD') {
      return amountAUDCents;
    }

    try {
      // Reverse conversion - convert from AUD to target currency
      if (window.Shopify && window.Shopify.currency) {
        if (window.Shopify.currency.rates && window.Shopify.currency.rates['AUD'] && window.Shopify.currency.rates[toCurrency]) {
          const audRate = window.Shopify.currency.rates['AUD'];
          const targetRate = window.Shopify.currency.rates[toCurrency];
          return Math.round((amountAUDCents / audRate) * targetRate);
        }
      }
    } catch (error) {
      console.error('Error converting from AUD:', error);
    }

    return amountAUDCents;
  }

  formatCurrency(cents, currency) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  async loadGiftImage() {
    const imageElement = document.querySelector('.cart-gift-card__image img[data-media-id]');
    if (!imageElement || imageElement.src) return; // Already loaded

    const mediaId = imageElement.dataset.mediaId;
    const productHandle = imageElement.dataset.productHandle;
    
    if (!mediaId) return;

    // Try product handle from data attribute first
    const productHandles = productHandle ? [productHandle] : ['playing-cards-black-white', 'playing-cards', 'pargo-playing-cards'];
    
    for (const handle of productHandles) {
      try {
        const response = await fetch(`/products/${handle}.json`);
        if (response.ok) {
          const data = await response.json();
          if (data.product) {
            // Find media by ID or use featured image
            const media = data.product.media?.find(m => m.id === parseInt(mediaId)) || data.product.media?.[0];
            if (media && media.src) {
              imageElement.src = media.src;
              imageElement.alt = data.product.title;
              return;
            } else if (data.product.featured_image) {
              imageElement.src = data.product.featured_image;
              imageElement.alt = data.product.title;
              return;
            }
          }
        }
      } catch (error) {
        continue;
      }
    }
    
    // If all else fails, try to construct URL from media ID
    // This is a fallback - you may need to adjust based on your Shopify setup
    console.warn('Could not load gift image from product. Please set the image src manually in the Liquid template.');
  }

  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
}

// Initialize when DOM is ready
let cartGiftProgressInstance = null;

function initCartGiftProgress() {
  if (cartGiftProgressInstance) {
    cartGiftProgressInstance.destroy();
  }
  cartGiftProgressInstance = new CartGiftProgress();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartGiftProgress);
} else {
  initCartGiftProgress();
}

// Re-initialize when cart drawer content is replaced
let reinitObserver = null;
let reinitTimeout = null;

document.addEventListener('DOMContentLoaded', () => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    // Debounce the re-initialization to prevent loops
    const debouncedReinit = typeof debounce !== 'undefined'
      ? debounce(() => {
          const progressElement = document.querySelector('.cart-gift-progress');
          if (progressElement && !progressElement.dataset.initialized) {
            progressElement.dataset.initialized = 'true';
            setTimeout(initCartGiftProgress, 50);
          }
        }, 200)
      : () => {
          if (reinitTimeout) clearTimeout(reinitTimeout);
          reinitTimeout = setTimeout(() => {
            const progressElement = document.querySelector('.cart-gift-progress');
            if (progressElement && !progressElement.dataset.initialized) {
              progressElement.dataset.initialized = 'true';
              setTimeout(initCartGiftProgress, 50);
            }
          }, 200);
        };

    reinitObserver = new MutationObserver(() => {
      debouncedReinit();
    });

    reinitObserver.observe(cartDrawer, {
      childList: true,
      subtree: true
    });
  }
});
